/**
 * Возврат с платформы: `#sso_code=…&state=…` → сессия модуля.
 *
 * Три вещи, каждая из которых чинит наблюдённую в platform_hub грабли:
 *
 * 1. Код читается из **fragment** и тут же стирается из истории
 *    (`history.replaceState`): он одноразовый, но в истории браузера и в шаринге
 *    ссылки ему делать нечего.
 * 2. `state` сверяется с тем, что положила кнопка, и удаляется сразу после чтения.
 * 3. Флаг «обмен начат» — **модульный**, а не в состоянии компонента: React в
 *    StrictMode монтирует эффект дважды, и второй проход не нашёл бы ни кода
 *    (его уже стёрли), ни state — экран показал бы ложную ошибку поверх успеха.
 *
 * Та же страница завершает вход MCP-клиента (`state = mcp.<txn>`): адрес возврата в
 * реестре платформы один. Эта ветка идёт ДО сверки `sessionStorage` и не трогает
 * его: `state` выдал сервер авторизации, браузер привязан к входу cookie экрана
 * согласия, а обычный вход, начатый в этой же вкладке, ломаться не должен.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Result, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  completeMcpAuthorization,
  exchangeSsoCode,
  transportErrorOf,
} from '../../shared/api/client';
import { useAuthStore } from '../../shared/session/authStore';
import {
  SSO_STATE_KEY,
  isMcpCallback,
  isSafeClientRedirect,
  readCallbackError,
  readCallbackFragment,
  readMcpCallback,
} from './ssoFlow';
import { beginSsoExchange, resetSsoExchangeGuard } from './ssoState';

type Phase = 'working' | 'returned' | 'failed';

const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;
const HTTP_BAD_GATEWAY = 502;

const PlatformSsoCallback: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('working');
  const [reason, setReason] = useState<string | null>(null);

  const fail = useCallback((text: string) => {
    setReason(text);
    setPhase('failed');
  }, []);

  useEffect(() => {
    if (!beginSsoExchange()) return;

    const hash = window.location.hash;
    // Код одноразовый и уже прочитан — в адресной строке ему не место.
    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    if (isMcpCallback(hash)) {
      const completion = readMcpCallback(hash);
      if (!completion) {
        fail(t('platform.ssoNoCode'));
        return;
      }
      completeMcpAuthorization(completion)
        .then(({ redirect_url: redirectUrl }) => {
          if (!isSafeClientRedirect(redirectUrl)) {
            fail(t('platform.mcpFailed', { reason: 'unsafe redirect_uri' }));
            return;
          }
          setPhase('returned');
          window.location.assign(redirectUrl);
        })
        .catch((error: unknown) => {
          const status = error instanceof ApiError ? error.status : undefined;
          // 403 — у браузера нет cookie этого входа (другой браузер или вход устарел),
          // 404 — вход протух или уже завершён: и то и другое чинится только новым
          // подключением из приложения, повтор здесь не поможет.
          if (status === HTTP_FORBIDDEN || status === HTTP_NOT_FOUND) {
            fail(t('platform.mcpBindingLost'));
          } else if (status === HTTP_BAD_GATEWAY) {
            fail(t('platform.ssoUnavailable'));
          } else {
            fail(t('platform.mcpFailed', { reason: error instanceof Error ? error.message : String(error) }));
          }
        });
      return;
    }

    const expectedState = sessionStorage.getItem(SSO_STATE_KEY);
    sessionStorage.removeItem(SSO_STATE_KEY);

    const platformError = readCallbackError(hash);
    if (platformError) {
      fail(t('platform.ssoPlatformError', { reason: platformError }));
      return;
    }

    const params = readCallbackFragment(hash);
    if (!params) {
      fail(t('platform.ssoNoCode'));
      return;
    }
    if (!expectedState || params.state !== expectedState) {
      // Ответ не принадлежит этому окну: продолжать — значит впустить чужой вход.
      fail(t('platform.ssoStateMismatch'));
      return;
    }

    exchangeSsoCode(params.code)
      .then(async (tokens) => {
        useAuthStore.getState().applyTokens(tokens.access_token, tokens.refresh_token);
        await useAuthStore.getState().fetchMe();
        navigate('/', { replace: true });
      })
      .catch((error) => {
        const status = transportErrorOf(error)?.response?.status;
        // 502 — платформа или наша регистрация, а не пользователь: текст обязан это
        // различать, иначе человек будет бесконечно жать «Войти».
        fail(status === 502 ? t('platform.ssoUnavailable') : t('platform.ssoRejected'));
      });
  }, [fail, navigate, t]);

  if (phase === 'working') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" tip={t('platform.ssoWorking')}>
          <div style={{ padding: 48 }} />
        </Spin>
      </div>
    );
  }

  if (phase === 'returned') {
    // Браузер уходит в клиент сам. Для `cursor://…` он остаётся на этой странице и
    // спрашивает разрешения открыть приложение — вечный спиннер здесь соврал бы.
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Card style={{ maxWidth: 520 }}>
          <Result status="success" title={t('platform.mcpReturning')} />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Card style={{ maxWidth: 520 }}>
        <Result
          status="warning"
          title={t('platform.ssoFailedTitle')}
          subTitle={reason}
          extra={
            <Button
              type="primary"
              onClick={() => {
                resetSsoExchangeGuard();
                navigate('/', { replace: true });
              }}
            >
              {t('platform.ssoBackToLogin')}
            </Button>
          }
        />
      </Card>
    </div>
  );
};

export default PlatformSsoCallback;
